// SANDBOX-1 — reading the artifact list.
//
// NOTHING HERE CREATES AN ARTIFACT. Generation is a later wave; this wave
// proves the container, and a container that can already be filled has not
// been proved empty.
//
// The table itself is PRINTED, NOT APPLIED (supabase/migrations/
// 20261101_sandbox_1_merchant_artifacts.sql), so until Tyler runs it this read
// fails, and the surface says exactly that. THAT IS THE POINT: GHOST-VISION
// law 2 — "we could not ask" and "we asked and there is nothing" must never
// look the same. This module therefore THROWS on a failed read rather than
// returning [], which is the mistake that would make a missing table look like
// an empty account.

import { supabase } from '../supabase'
import {
  decideChange,
  type ArtifactVersion,
  type ChangeOutcome,
  type Rebuild,
} from '../artifactChange'

export interface MerchantArtifact {
  id: string
  slug: string
  name: string
  content: string
  kind: string
  status: string
  created_at: string
}

const COLUMNS = 'id, slug, name, content, kind, status, created_at'

/** Postgres says 42P01 when a relation does not exist. */
const UNDEFINED_TABLE = '42P01'

/**
 * This account's live artifacts, newest first.
 *
 * THROWS on any read failure, including the table not existing yet. The caller
 * renders the failure; it never renders it as emptiness.
 */
export async function listArtifacts(subAccountId: string): Promise<MerchantArtifact[]> {
  // The table is not in the generated types until the migration is applied.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const res = await sb
    .from('merchant_artifacts')
    .select(COLUMNS)
    .eq('sub_account_id', subAccountId)
    .is('deleted_at', null)
    .order('created_at', { ascending: false })

  if (res.error) {
    if (res.error.code === UNDEFINED_TABLE) {
      throw new Error(
        'The artifacts table has not been created yet. Its migration is printed at ' +
          'supabase/migrations/20261101_sandbox_1_merchant_artifacts.sql and has not been applied.',
      )
    }
    throw new Error(res.error.message || 'The artifact list could not be read.')
  }

  // A successful read of zero rows. Different from the throw above, and the
  // surface shows it differently.
  return (res.data ?? []) as MerchantArtifact[]
}

// ─── ARTIFACT-GEN-1 ────────────────────────────────────────────────────

export interface GeneratedArtifact {
  artifact: MerchantArtifact
  shape: string
  tokens: number
  charge_cents?: number
}

export interface GenerationRefusal {
  error: string
  violations: Array<{ name: string; outbound: boolean; line: number; excerpt: string }>
  charged: boolean
  /** The permission to go and ask for, when the refusal was a permission. */
  needs?: string | null
}

export type GenerationResult =
  | { ok: true; generated: GeneratedArtifact }
  | { ok: false; refusal: GenerationRefusal }

/**
 * Ask Ghost to build an artifact from a sentence.
 *
 * ONE STEP, AND DELIBERATELY NOT A PLAN. PLAN-ENGINE-1 exists for work that is
 * genuinely multi step - several actions, in order, any of which can stop and
 * wait for the merchant. Generating an artifact is one call that either
 * produces a validated document or does not. Wrapping it in a plan would add
 * an approval gate in front of a single action that the merchant just asked
 * for out loud, and a resume mechanism for a step that cannot pause. The wave
 * said not to force a plan around one step, and this is one step.
 *
 * A REFUSAL IS NOT AN ERROR TO SWALLOW. The function returns 422 with the
 * violations when the model produced something that reaches outward, and those
 * come back to the merchant as the reason.
 */
export async function generateArtifact(
  subAccountId: string,
  request: string,
): Promise<GenerationResult> {
  const { data: sessionData } = await supabase.auth.getSession()
  const token = sessionData.session?.access_token
  if (!token) throw new Error('You are signed out. Sign in and try again.')

  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-artifact`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ sub_account_id: subAccountId, request }),
  })
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null
  if (!body) throw new Error('Ghost did not answer. Try again.')

  if (res.ok && body.ok === true) {
    return { ok: true, generated: body as unknown as GeneratedArtifact }
  }
  // NOBODY-PAYS-FOR-NOTHING-1 — A PERMISSION IS AN ANSWER, NOT A CRASH.
  //
  // 422 is "it was built and it broke a rule"; 403 is "you may not keep this
  // here, so nothing was built and nothing was spent". Both are Ghost telling
  // a person something true, and both must reach them as words. Throwing the
  // 403 sent the merchant's own answer through the panel's "Ghost could not be
  // reached" path, which is the one sentence that is not true about it.
  if (res.status === 422 || res.status === 403) {
    return {
      ok: false,
      refusal: {
        error: String(body.error ?? 'the artifact could not be built'),
        violations: (body.violations ?? []) as GenerationRefusal['violations'],
        charged: false,
        needs: typeof body.needs === 'string' ? body.needs : null,
      },
    }
  }
  throw new Error(String(body.error ?? `Generation failed (${res.status}).`))
}

/**
 * The artifact rendered. Promote it from draft to active.
 *
 * This is the ONLY path to 'active', and it is reachable only from
 * ArtifactRenderProbe reporting that the frame said ready. Nothing else in the
 * product may call an artifact finished.
 */
export async function markArtifactRenders(id: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const { error } = await sb.from('merchant_artifacts').update({ status: 'active' }).eq('id', id)
  if (error) throw new Error(error.message || 'Could not save the artifact.')
}

/**
 * IMAGE-REPLACE-1 — write the document back after a picture was replaced.
 *
 * The caller's credential, so RLS decides whose artifact this is - the same
 * rule the insert follows. Nothing here validates the html: what may be written
 * is decided by replaceImage, which refuses a remote address and anything over
 * the size ceiling, and this is the only path that calls it.
 */
export async function saveArtifactContent(id: string, content: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const { error } = await sb.from('merchant_artifacts').update({ content }).eq('id', id)
  if (error) throw new Error(error.message || 'Could not save the picture.')
}

/**
 * The artifact does not render. Remove it.
 *
 * A generated document that throws or paints nothing is a FAILED generation,
 * so it does not sit in the list as a draft the merchant has to tidy up. It is
 * soft removed on the spot and the merchant is told why. Nothing half built is
 * left behind for somebody to find later and wonder about.
 */
export async function removeArtifact(id: string): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any
  const { error } = await sb
    .from('merchant_artifacts')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw new Error(error.message || 'Could not remove the artifact.')
}

// ─── SANDBOX-2 ─────────────────────────────────────────────────────────

/**
 * Mint a short-lived URL that serves this artifact under the deny-all policy.
 *
 * TWO STEPS, AND THE FIRST ONE IS THE BOUNDARY. The POST carries the caller's
 * session, and serve-artifact reads the artifact with the CALLER'S OWN client -
 * so RLS decides whether this person may see it, exactly as it does for every
 * other read of the table. Only then is a token minted.
 *
 * THE ACCOUNT IS NEVER SENT. There is no sub_account_id in this request and
 * there is no room for one: the function's mint takes (caller, artifactId).
 * The same law as the read door, holding here.
 *
 * The returned URL is a bare GET, which is what an iframe src has to be - a
 * frame cannot carry an Authorization header. Its credential is the token,
 * which lives 60 seconds and names only the artifact id.
 */
export async function mintArtifactViewUrl(artifactId: string): Promise<string> {
  const { data: sessionData } = await supabase.auth.getSession()
  const token = sessionData.session?.access_token
  if (!token) throw new Error('You are signed out. Sign in and try again.')

  const base = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/serve-artifact`
  const res = await fetch(base, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ artifact_id: artifactId }),
  })
  const body = (await res.json().catch(() => null)) as {
    ok?: boolean
    token?: string
    error?: string
  } | null
  if (!res.ok || !body?.ok || !body.token) {
    // A failed mint is a FAILED READ and says so. It never falls back to
    // inlining the content, because an inlined document does not carry the
    // policy and the merchant would be looking at a box with the gap reopened.
    throw new Error(body?.error ?? `Could not open this artifact (${res.status}).`)
  }
  return `${base}?t=${encodeURIComponent(body.token)}`
}

/**
 * CHANGE-WHAT-WAS-BUILT-1 — rebuild a thing that already exists, in place.
 *
 * SAME ROW, SAME ID, SAME SLUG, so a link the merchant already shared keeps
 * working. The content that was serving is written as a version FIRST, and the
 * artifact is only updated once that has landed — a version lost is an undo the
 * merchant was promised and cannot have.
 *
 * THE HONESTY LAW. A rebuild that fails, or comes back empty, leaves the
 * previous version serving and returns the sentence saying so. The decision is
 * `decideChange`'s, not this function's; this only carries out what it says.
 */
export async function changeArtifact(
  subAccountId: string,
  artifact: Pick<MerchantArtifact, 'id' | 'content'>,
  request: string,
  rebuild: Rebuild,
): Promise<ChangeOutcome> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sb = supabase as any

  const { data: rows, error: readErr } = await sb
    .from('merchant_artifact_versions')
    .select('n, content, request, created_at')
    .eq('artifact_id', artifact.id)
    .order('n', { ascending: true })
  // A HISTORY WE COULD NOT READ IS NOT AN EMPTY HISTORY. Treating it as empty
  // would number the next version 1 and collide with one already there.
  if (readErr) throw new Error(readErr.message || 'Could not read this thing’s history.')

  const versions: ArtifactVersion[] = (rows ?? []).map(
    (r: { n: number; content: string; request: string; created_at: string }) => ({
      n: r.n,
      content: r.content,
      request: r.request,
      when: r.created_at,
    }),
  )

  const outcome = decideChange(
    {
      artifactId: artifact.id,
      artifactAccount: subAccountId,
      callerAccount: subAccountId,
      request,
    },
    { content: artifact.content, versions },
    rebuild,
  )
  // Nothing is written unless the decision says the thing changed. A refusal
  // and a kept-previous both leave the row exactly as it was.
  if (outcome.state !== 'changed') return outcome

  const { error: keepErr } = await sb.from('merchant_artifact_versions').insert({
    artifact_id: artifact.id,
    sub_account_id: subAccountId,
    n: outcome.keep.n,
    content: outcome.keep.content,
    request: outcome.keep.request,
  })
  if (keepErr) throw new Error(keepErr.message || 'Could not save the version you had.')

  const { error: updErr } = await sb
    .from('merchant_artifacts')
    .update({ content: outcome.content, updated_at: new Date().toISOString() })
    .eq('id', artifact.id)
  if (updErr) throw new Error(updErr.message || 'Could not save the change.')

  return outcome
}
